/**
 * audio.js -- Web Audio output fed from the core's ring buffer.
 *
 * Design note: this uses a ScriptProcessorNode-free approach based on
 * AudioWorklet where available, falling back to a periodically-scheduled
 * AudioBufferSourceNode queue otherwise.
 *
 * ...that is what you might expect, but it is not what this does, and the
 * reason matters. An AudioWorklet runs on the audio thread and cannot call
 * into the wasm core (the core is driven from the main thread's rAF loop and
 * is not thread-safe or SharedArrayBuffer-backed here). So the main thread
 * pulls samples out of the core's ring after each emulated frame and schedules
 * them as small AudioBuffers slightly ahead of the playback clock. That keeps
 * all core access on one thread and needs no cross-origin isolation headers.
 *
 * The cost is latency: we hold a small queue (target ~100ms) so a late rAF
 * tick does not underrun. That is acceptable for an emulator and is the same
 * trade most browser emulator frontends make without SharedArrayBuffer.
 */

const TARGET_LATENCY = 0.1; // seconds of audio we try to keep scheduled ahead
const MAX_LATENCY = 0.25; // beyond this we drop samples to catch up
const CHUNK_FRAMES = 1024; // frames per scheduled AudioBuffer

export class AudioOutput {
  constructor() {
    this.ctx = null;
    this.gainNode = null;
    this.sampleRate = 32040;
    this.playHead = 0; // absolute AudioContext time the queue reaches to
    this.enabled = true;
    this.muted = false;
    this.fastForward = false;
    this.fastForwardRate = 1; // the speed multiplier passed to the most recent setFastForward(true, ...)
    this.audioDuringFastForward = false; // user preference: play (pitched-up) audio while fast-forwarding, rather than muting it

    this._left = new Float32Array(CHUNK_FRAMES);
    this._right = new Float32Array(CHUNK_FRAMES);
  }

  /**
   * Create the AudioContext. Must be called from a user gesture, or the
   * context starts suspended (autoplay policy) -- see resume().
   */
  start(sampleRate) {
    if (this.ctx) return;

    this.sampleRate = sampleRate || this.sampleRate;

    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) {
      this.enabled = false;
      console.warn('[audio] Web Audio unavailable; running silent');
      return;
    }

    // Ask for the core's native rate so no resampling is needed. Browsers may
    // refuse and give their own device rate; the AudioBuffer we create still
    // declares the core's rate, and the browser resamples it for us.
    try {
      this.ctx = new Ctor({ sampleRate: this.sampleRate, latencyHint: 'interactive' });
    } catch {
      this.ctx = new Ctor({ latencyHint: 'interactive' });
    }

    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = this._silent ? 0 : 1;
    this.gainNode.connect(this.ctx.destination);

    this.playHead = this.ctx.currentTime;
  }

  async resume() {
    if (this.ctx && this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch (err) {
        console.warn('[audio] resume refused:', err?.message ?? err);
      }
    }
  }

  /** True while output should actually be silent, independent of *why*. */
  get _silent() {
    return this.muted || (this.fastForward && !this.audioDuringFastForward);
  }

  _syncGain() {
    if (this.gainNode) this.gainNode.gain.value = this._silent ? 0 : 1;
  }

  setMuted(muted) {
    this.muted = muted;
    this._syncGain();
  }

  /**
   * Called once per frame-pacing change from main.js's tick(). `rate` is the
   * same speed multiplier tick() applies to the frame accumulator (currently
   * always FAST_FORWARD_MULTIPLIER) -- pump() uses it to play scheduled
   * buffers back at that same rate via AudioBufferSourceNode.playbackRate
   * when audioDuringFastForward is on, the same "condensed"/pitched-up
   * technique RetroArch uses for fast-forward audio rather than dropping
   * samples or muting outright. Whether that's actually audible right now is
   * controlled separately by setAudioDuringFastForward() -- see _silent.
   */
  setFastForward(active, rate = 1) {
    this.fastForward = active;
    this.fastForwardRate = rate;
    this._syncGain();
  }

  /** User preference (a checkbox in the UI): hear pitched-up audio while fast-forwarding, instead of silence. */
  setAudioDuringFastForward(enabled) {
    this.audioDuringFastForward = enabled;
    this._syncGain();
  }

  get state() {
    return this.ctx?.state ?? 'uninitialised';
  }

  /**
   * Pull whatever the core has produced and schedule it for playback.
   * Call once per emulated frame.
   */
  pump(core) {
    if (!this.enabled || !this.ctx || this.ctx.state !== 'running') {
      // Keep the core's ring from filling up and stalling its producer while
      // audio is suspended (before the first user gesture, or on a muted tab).
      core.flushAudio();
      return;
    }

    const now = this.ctx.currentTime;

    // A tab that was backgrounded leaves playHead far in the past; snapping
    // forward avoids scheduling a burst of buffers that all play at once.
    if (this.playHead < now) this.playHead = now;

    // Too far ahead: the emulator is outrunning the audio clock. Drop the
    // backlog rather than growing latency without bound.
    if (this.playHead - now > MAX_LATENCY) {
      core.flushAudio();
      return;
    }

    // While fast-forwarding with audioDuringFastForward on, play buffers back
    // at the same multiplier the emulator itself is sped up by -- since the
    // core is also producing audio that many times faster (tick() scales the
    // frame accumulator by the same rate), this keeps playback consumption
    // matched to production instead of latency drifting either direction.
    // Content isn't skipped or resampled, just played faster/higher-pitched,
    // the same "condensed" effect RetroArch's fast-forward audio has.
    const rate = this.fastForward && this.audioDuringFastForward ? this.fastForwardRate : 1;

    while (this.playHead - now < TARGET_LATENCY) {
      const got = core.readAudio(this._left, this._right, CHUNK_FRAMES);
      if (got <= 0) break;

      const buf = this.ctx.createBuffer(2, got, this.sampleRate);
      buf.copyToChannel(this._left.subarray(0, got), 0);
      buf.copyToChannel(this._right.subarray(0, got), 1);

      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = rate;
      src.connect(this.gainNode);
      src.start(this.playHead);

      this.playHead += got / this.sampleRate / rate;
    }
  }

  /** Seconds of audio currently scheduled but not yet played. */
  get latency() {
    if (!this.ctx) return 0;
    return Math.max(0, this.playHead - this.ctx.currentTime);
  }

  async close() {
    if (this.ctx) {
      try {
        await this.ctx.close();
      } catch {
        /* already closed */
      }
      this.ctx = null;
      this.gainNode = null;
    }
  }
}
